import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/api"

export interface MarketOpportunity {
  id: string
  title: string
  source: string
  source_url: string
  published_date: string
  product_tags: string
  summary: string
  score: number
  status: "novo" | "criacao" | "aprovacao" | "publicado"
  approval_link: string | null
  worked_by_name: string | null
  worked_at: string | null
  lead_id: string | null
  search_batch: string
  created_at: string
}

export interface MarketStats {
  total: number
  by_status: Record<string, number>
  by_product: Record<string, number>
  by_source: Record<string, number>
}

export function useMarketOpportunities(params: {
  status?: string
  product?: string
  source?: string
  score_min?: number
  search?: string
  page?: number
  per_page?: number
}) {
  return useQuery({
    queryKey: ["market-opportunities", params],
    queryFn: async () => {
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== "" && v !== null) searchParams.append(k, String(v))
      })
      const { data } = await api.get(`/v1/market-analysis/?${searchParams}`)
      return data as { items: MarketOpportunity[]; total: number; page: number; per_page: number }
    },
    staleTime: 60 * 1000,
  })
}

export function useMarketStats() {
  return useQuery({
    queryKey: ["market-stats"],
    queryFn: async () => {
      const { data } = await api.get("/v1/market-analysis/stats")
      return data as MarketStats
    },
    staleTime: 2 * 60 * 1000,
  })
}

export function useUpdateMarketStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, status, approval_link }: { id: string; status: string; approval_link?: string }) => {
      const { data } = await api.put(`/v1/market-analysis/${id}/status`, { status, approval_link })
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["market-opportunities"] })
      qc.invalidateQueries({ queryKey: ["market-stats"] })
    },
  })
}

export function useCreateLeadFromOpportunity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/v1/market-analysis/${id}/create-lead`)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["market-opportunities"] })
      qc.invalidateQueries({ queryKey: ["market-stats"] })
    },
  })
}

export function useTriggerMarketSearch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: { date_from: string; date_to: string }) => {
      const { data } = await api.post("/v1/market-analysis/search", params)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["market-opportunities"] })
      qc.invalidateQueries({ queryKey: ["market-stats"] })
    },
  })
}


export function useTranslateOpportunity() {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/v1/market-analysis/${id}/translate`)
      return data as {
        pt: { title: string; summary: string }
        en: { title: string; summary: string }
        es: { title: string; summary: string }
      }
    },
  })
}

// ── Market Analysis Settings ──

export interface MarketSource {
  id: string
  url: string
  name: string
  type: string
  location: string
}

export interface MarketSchedule {
  days: number[]
  time: string
  enabled: boolean
}

export interface MarketLog {
  id: string
  timestamp: string
  status: string
  details: {
    total_found?: number
    created?: number
    errors?: number
    duration_seconds?: number
    date_from?: string
    date_to?: string
  }
}

export function useMarketSources() {
  return useQuery({
    queryKey: ["market-sources"],
    queryFn: async () => {
      const { data } = await api.get("/v1/market-analysis/settings/sources")
      return data as MarketSource[]
    },
  })
}

export function useAddMarketSource() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (source: { url: string; name: string; type: string; location: string }) => {
      const { data } = await api.post("/v1/market-analysis/settings/sources", source)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["market-sources"] }),
  })
}

export function useDeleteMarketSource() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/v1/market-analysis/settings/sources/${id}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["market-sources"] }),
  })
}

export function useMarketSchedule() {
  return useQuery({
    queryKey: ["market-schedule"],
    queryFn: async () => {
      const { data } = await api.get("/v1/market-analysis/settings/schedule")
      return data as MarketSchedule
    },
  })
}

export function useUpdateMarketSchedule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (schedule: MarketSchedule) => {
      const { data } = await api.put("/v1/market-analysis/settings/schedule", schedule)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["market-schedule"] }),
  })
}

export function useMarketLogs() {
  return useQuery({
    queryKey: ["market-logs"],
    queryFn: async () => {
      const { data } = await api.get("/v1/market-analysis/settings/logs")
      return data as MarketLog[]
    },
  })
}
